import Loading from '@/common/atoms/Loading'
import TextError from '@/common/atoms/TextError'
import { Heading, Select, Text, useToast, VStack } from '@chakra-ui/react'
import {
  useAdminImportReleasesMutation,
  useAdminReleasesSubscription,
} from '@gql'
import { parseVersion } from '@rolebase/shared/model/release'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trpc } from 'src/trpc'
import { importFromBundled } from '../releaseBoard'
import AdminReleaseEditor from '../components/AdminReleaseEditor'

// Newest version first, by number (v10.0.0 after v9.0.0)
function byVersionDesc(a: { version: string }, b: { version: string }) {
  const va = parseVersion(a.version)
  const vb = parseVersion(b.version)
  if (!va || !vb) return b.version.localeCompare(a.version)
  return vb.major - va.major || vb.minor - va.minor || vb.patch - va.patch
}

// #77 — the console's « Nouveautés » page. The tickets of every version come
// from the backend (written at delivery time); they are imported here, then
// Yohan sorts them like the compte rendu sharing, gives the version a title
// and publishes it. Nothing is visible to members before.
export default function AdminReleasesPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const { data, loading, error } = useAdminReleasesSubscription()
  const [importReleases] = useAdminImportReleasesMutation()

  // Import once, when the database is known: only what is missing is added
  const imported = useRef(false)
  useEffect(() => {
    if (!data || imported.current) return
    imported.current = true
    trpc.release.getBundledReleases
      .query()
      .then((bundled) => {
        const { releases, items } = importFromBundled(bundled, data.release)
        if (releases.length === 0 && items.length === 0) return
        return importReleases({ variables: { releases, items } })
      })
      .catch((error) =>
        toast({
          title: t('common.error'),
          description: error?.message,
          status: 'error',
        })
      )
  }, [data])

  const releases = useMemo(
    () => [...(data?.release ?? [])].sort(byVersionDesc),
    [data]
  )
  const [version, setVersion] = useState<string>()

  // What the last publish or unpublish wrote, shown at once: the page does not
  // wait for the subscription to bring it back.
  const [published, setPublished] = useState<Record<string, string | null>>({})
  const withPublished = releases.map((r) =>
    r.version in published ? { ...r, publishedAt: published[r.version] } : r
  )
  const release =
    withPublished.find((r) => r.version === version) ?? withPublished[0]

  return (
    <>
      <Heading size="md" mb={6}>
        {t('SuperAdmin.releases.heading')}
      </Heading>

      {loading && <Loading active center />}
      {error && <TextError error={error} />}

      {data && !release && (
        <Text color="gray.500">{t('SuperAdmin.releases.empty')}</Text>
      )}

      {release && (
        <VStack align="stretch" spacing={5}>
          <Select
            maxW="320px"
            value={release.version}
            onChange={(e) => setVersion(e.target.value)}
            aria-label={t('SuperAdmin.releases.version')}
          >
            {withPublished.map((r) => (
              <option key={r.version} value={r.version}>
                {r.version} ·{' '}
                {r.publishedAt
                  ? t('SuperAdmin.releases.published')
                  : t('SuperAdmin.releases.notPublished')}
              </option>
            ))}
          </Select>

          <AdminReleaseEditor
            key={release.version}
            release={release}
            onPublishedChange={(publishedAt) =>
              setPublished((current) => ({
                ...current,
                [release.version]: publishedAt,
              }))
            }
          />
        </VStack>
      )}
    </>
  )
}
